import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import TextField from '@material-ui/core/TextField';
import { useEffect, useState } from 'react';

/**
 * `ui:options` accepted by the DerivedText field.
 */
export type DerivedTextUiOptions = {
  /**
   * Name of another form field whose value prefills this one. The source may
   * sit on an earlier page of the form.
   */
  derivedFrom: string;
  /**
   * Optional shape for the prefilled value, with `{value}` standing for the
   * source field's value. Defaults to the source value itself.
   */
  template?: string;
};

/**
 * A text field that starts out as a value computed from another field and
 * becomes an ordinary editable field the moment the person types in it.
 *
 * Until then it follows the source: change the service name on page one and
 * the domain on page three changes with it. Once edited, the person's value
 * wins and the source is ignored, so going back to fix a typo in the service
 * name never overwrites a hostname someone chose on purpose.
 */
export const DerivedTextField = (
  props: FieldExtensionComponentProps<string, DerivedTextUiOptions>,
) => {
  const {
    formData,
    onChange,
    formContext,
    rawErrors,
    required,
    schema,
    uiSchema,
    idSchema,
    disabled,
    readonly,
  } = props;
  const options = uiSchema['ui:options'] ?? ({} as DerivedTextUiOptions);
  const source = formContext?.formData?.[options.derivedFrom];
  const derived =
    typeof source === 'string' && source !== ''
      ? (options.template ?? '{value}').replace('{value}', source)
      : '';
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (!edited && derived !== '' && formData !== derived) {
      onChange(derived);
    }
  }, [derived, edited, formData, onChange]);

  return (
    <TextField
      id={idSchema?.$id}
      label={schema.title}
      helperText={rawErrors?.length ? rawErrors.join(' ') : schema.description}
      error={Boolean(rawErrors?.length)}
      required={required}
      disabled={disabled}
      InputProps={{ readOnly: readonly }}
      value={formData ?? ''}
      onChange={event => {
        setEdited(true);
        onChange(event.target.value);
      }}
      margin="normal"
      fullWidth
    />
  );
};
